import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Modal, 
  Form, 
  Alert, 
  Table,
  Badge,
  Image
} from 'react-bootstrap';
import axios from 'axios';

const StaffManager = () => {
  const [staff, setStaff] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    ruolo: '',
    email: '',
    telefono: '',
    bio: '',
    specializzazioni: '',
    genere: 'maschio',
    immagine: null,
    // Campi per traduzioni inglesi
    nome_en: '',
    cognome_en: '',
    ruolo_en: '',
    bio_en: '',
    specializzazioni_en: ''
  });

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://cogeis.onrender.com';
      const response = await axios.get(`${apiUrl}/api/staff?lang=it`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaff(response.data.data);
    } catch (err) {
      setError('Errore nel caricamento dello staff');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    
    // Pulisci l'errore del campo quando l'utente inizia a digitare
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    if (name === 'immagine' && files[0]) {
      setFormData({ ...formData, immagine: files[0] });
      setImagePreview(URL.createObjectURL(files[0]));
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset errori
    setError('');
    setFieldErrors({});
    
    // Validazione lato client per i campi obbligatori
    const newFieldErrors = {};
    if (!formData.nome || formData.nome.trim() === '') {
      newFieldErrors.nome = 'Il nome è obbligatorio';
    }
    if (!formData.cognome || formData.cognome.trim() === '') {
      newFieldErrors.cognome = 'Il cognome è obbligatorio';
    }
    if (!formData.ruolo || formData.ruolo.trim() === '') {
      newFieldErrors.ruolo = 'Il ruolo è obbligatorio';
    }
    
    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      setError('Per favore correggi gli errori nei campi obbligatori');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
      
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (editingMember) {
        await axios.put(
          `${process.env.REACT_APP_API_URL || 'https://cogeis.onrender.com'}/api/staff/${editingMember._id}`,
          formDataToSend,
          {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        setSuccess('Membro dello staff aggiornato con successo!');
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL || 'https://cogeis.onrender.com'}/api/staff`,
          formDataToSend,
          {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        setSuccess('Membro dello staff creato con successo!');
      }

      handleCloseModal();
      fetchStaff();
    } catch (err) {
      console.error('Errore completo:', err);
      
      // Gestione errori di validazione dal backend
      if (err.response?.data?.errors) {
        const backendErrors = {};
        err.response.data.errors.forEach(error => {
          if (error.path) {
            backendErrors[error.path] = error.msg;
          }
        });
        setFieldErrors(backendErrors);
        setError('Per favore correggi gli errori nei campi');
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Errore durante il salvataggio. Controlla i dati inseriti.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      nome: member.nome,
      cognome: member.cognome,
      ruolo: member.ruolo,
      email: member.email,
      telefono: member.telefono,
      bio: member.bio,
      specializzazioni: member.specializzazioni,
      genere: member.genere || 'maschio',
      immagine: null,
      // Campi per traduzioni inglesi
      nome_en: member.nome_en || '',
      cognome_en: member.cognome_en || '',
      ruolo_en: member.ruolo_en || '',
      bio_en: member.bio_en || '',
      specializzazioni_en: member.specializzazioni_en || ''
    });
    setImagePreview(member.immagine);
    setShowModal(true);
  };

  const handleDelete = async (memberId) => {
    if (window.confirm('Sei sicuro di voler eliminare questo membro dello staff?')) {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        await axios.delete(
          `${process.env.REACT_APP_API_URL || 'https://cogeis.onrender.com'}/api/staff/${memberId}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setSuccess('Membro dello staff eliminato con successo!');
        fetchStaff();
      } catch (err) {
        setError('Errore durante l\'eliminazione del membro dello staff');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingMember(null);
    setFormData({
      nome: '',
      cognome: '',
      ruolo: '',
      email: '',
      telefono: '',
      bio: '',
      specializzazioni: '',
      genere: 'maschio',
      immagine: null
    });
    setImagePreview(null);
    setError('');
    setFieldErrors({});
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2>Gestione Staff</h2>
              {staff.length > 0 && (
                <p className="text-muted mb-0">
                  Totale membri: <strong>{staff.length}</strong>
                </p>
              )}
            </div>
            <Button 
              variant="primary" 
              onClick={() => setShowModal(true)}
              disabled={loading}
            >
              <i className="fas fa-plus me-2"></i>
              Nuovo Membro
            </Button>
          </div>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Row>
        <Col>
          <Card>
            <Card.Body>
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Caricamento...</span>
                  </div>
                </div>
              ) : (
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>Immagine</th>
                      <th>Nome</th>
                      <th>Cognome</th>
                      <th>Ruolo</th>
                      <th>Genere</th>
                      <th>Email</th>
                      <th>Telefono</th>
                      <th>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staff.map((member) => (
                      <tr key={member._id}>
                        <td>
                          {member.immagine && (
                            <Image 
                              src={member.immagine} 
                              alt={`${member.nome} ${member.cognome}`}
                              width="50"
                              height="50"
                              className="rounded"
                              style={{ objectFit: 'cover' }}
                            />
                          )}
                        </td>
                        <td>{member.nome}</td>
                        <td>{member.cognome}</td>
                        <td>
                          <Badge bg="info">{member.ruolo}</Badge>
                        </td>
                        <td>
                          <Badge bg={member.genere === 'femmina' ? 'pink' : 'primary'}>
                            {member.genere === 'femmina' ? 'Femmina' : 'Maschio'}
                          </Badge>
                        </td>
                        <td>{member.email}</td>
                        <td>{member.telefono}</td>
                        <td>
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            className="me-2"
                            onClick={() => handleEdit(member)}
                          >
                            <i className="fas fa-edit"></i>
                          </Button>
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => handleDelete(member._id)}
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal per aggiunta/modifica membro staff */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingMember ? 'Modifica Membro Staff' : 'Nuovo Membro Staff'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nome <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    required
                    isInvalid={!!fieldErrors.nome}
                  />
                  {fieldErrors.nome && (
                    <Form.Control.Feedback type="invalid">
                      {fieldErrors.nome}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cognome <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="cognome"
                    value={formData.cognome}
                    onChange={handleInputChange}
                    required
                    isInvalid={!!fieldErrors.cognome}
                  />
                  {fieldErrors.cognome && (
                    <Form.Control.Feedback type="invalid">
                      {fieldErrors.cognome}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Ruolo <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="ruolo"
                    value={formData.ruolo}
                    onChange={handleInputChange}
                    required
                    isInvalid={!!fieldErrors.ruolo}
                  />
                  {fieldErrors.ruolo && (
                    <Form.Control.Feedback type="invalid">
                      {fieldErrors.ruolo}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    isInvalid={!!fieldErrors.email}
                  />
                  {fieldErrors.email && (
                    <Form.Control.Feedback type="invalid">
                      {fieldErrors.email}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Telefono</Form.Label>
              <Form.Control
                type="tel"
                name="telefono"
                value={formData.telefono}
                onChange={handleInputChange}
                isInvalid={!!fieldErrors.telefono}
              />
              {fieldErrors.telefono && (
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.telefono}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Biografia</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                isInvalid={!!fieldErrors.bio}
              />
              {fieldErrors.bio && (
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.bio}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Specializzazioni</Form.Label>
              <Form.Control
                type="text"
                name="specializzazioni"
                value={formData.specializzazioni}
                onChange={handleInputChange}
                placeholder="Es: Architettura residenziale, Design d'interni, Ristrutturazioni"
                isInvalid={!!fieldErrors.specializzazioni}
              />
              {fieldErrors.specializzazioni && (
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.specializzazioni}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            {/* Sezione Traduzioni Inglesi */}
            <hr className="my-4" />
            <h6 className="text-muted mb-3">Traduzioni Inglesi (opzionali)</h6>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nome (EN)</Form.Label>
                  <Form.Control
                    type="text"
                    name="nome_en"
                    value={formData.nome_en}
                    onChange={handleInputChange}
                    placeholder="Es: Francis"
                    isInvalid={!!fieldErrors.nome_en}
                  />
                  {fieldErrors.nome_en && (
                    <Form.Control.Feedback type="invalid">
                      {fieldErrors.nome_en}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cognome (EN)</Form.Label>
                  <Form.Control
                    type="text"
                    name="cognome_en"
                    value={formData.cognome_en}
                    onChange={handleInputChange}
                    placeholder="Es: Smith"
                    isInvalid={!!fieldErrors.cognome_en}
                  />
                  {fieldErrors.cognome_en && (
                    <Form.Control.Feedback type="invalid">
                      {fieldErrors.cognome_en}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Ruolo (EN)</Form.Label>
              <Form.Control
                type="text"
                name="ruolo_en"
                value={formData.ruolo_en}
                onChange={handleInputChange}
                placeholder="Es: Web Developer"
                isInvalid={!!fieldErrors.ruolo_en}
              />
              {fieldErrors.ruolo_en && (
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.ruolo_en}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Biografia (EN)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="bio_en"
                value={formData.bio_en}
                onChange={handleInputChange}
                placeholder="Es: IT expert with 10 years of experience"
                isInvalid={!!fieldErrors.bio_en}
              />
              {fieldErrors.bio_en && (
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.bio_en}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Specializzazioni (EN)</Form.Label>
              <Form.Control
                type="text"
                name="specializzazioni_en"
                value={formData.specializzazioni_en}
                onChange={handleInputChange}
                placeholder="Es: Residential architecture, Interior design, Renovations"
                isInvalid={!!fieldErrors.specializzazioni_en}
              />
              {fieldErrors.specializzazioni_en && (
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.specializzazioni_en}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Genere</Form.Label>
              <Form.Select
                name="genere"
                value={formData.genere}
                onChange={handleInputChange}
                isInvalid={!!fieldErrors.genere}
              >
                <option value="maschio">Maschio</option>
                <option value="femmina">Femmina</option>
              </Form.Select>
              {fieldErrors.genere && (
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.genere}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Immagine</Form.Label>
              <Form.Control
                type="file"
                name="immagine"
                onChange={handleInputChange}
                accept="image/*"
              />
              {imagePreview && (
                <div className="mt-2">
                  <Image 
                    src={imagePreview} 
                    alt="Preview" 
                    width="200" 
                    className="rounded"
                  />
                </div>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Annulla
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Salvataggio...' : (editingMember ? 'Aggiorna' : 'Crea')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default StaffManager; 